import { Component, Input, ViewChild } from '@angular/core';
import { CarouselComponent } from './carousel/carousel.component';
import { SnackbarService } from '../../shared/components/snackbar/service/snackbar.service';
import { getE } from 'src/app/core/helpers/object.helper';

interface ControllerSelected {
  index: number;
  value: string;
}

interface CarouselItem {
  name: string;
  linkModal: string;
  linkHtml: string;
  linkThumbnail: string;
  alt: string;
}

@Component({
  selector: 'app-product-gallery',
  templateUrl: './product-gallery.component.html',
  styleUrls: ['./product-gallery.component.scss'],
})
export class ProductGalleryComponent {
  static CONTROLLERS = [
    // photos:
    {
      key: 'photos.length',
      controller: (product) => ({
        label: `Ver ${product?.photos.length} fotos`,
        icon: 'image_ico',
        value: 'photos-carousel',
      }),
    },
    // video:
    {
      key: 'videos.length',
      controller: {
        label: 'Ver vídeo',
        icon: 'video_camera_ico',
        value: 'videos',
      },
    },
    // map:
    {
      key: 'map',
      controller: {
        label: 'Ver mapa',
        icon: 'map_ico',
        value: 'map',
      },
    },
    // streetView:
    {
      key: 'map',
      controller: {
        label: 'Street view',
        icon: 'street_view_ico',
        value: 'street-view',
      },
    },
    // plans:
    {
      key: 'floor_plans',
      controller: {
        label: 'Ver plantas',
        icon: 'see_plans_ico',
        value: 'floor_plans',
      },
    },
    // media 360
    {
      key: 'tour360.length',
      controller: {
        label: 'Ver 360',
        icon: 'see_plans_ico',
        value: 'tour360',
      },
    },
  ];

  @ViewChild('plansView') plansView: CarouselComponent;

  public instanceDocument = document;

  private rawProduct;

  @Input() set product(product) {
    this.rawProduct = product;
    this.init(product);
  }
  get product() {
    return this.rawProduct;
  }

  @Input() viewBookmark = true;

  loaded = false;
  enableShareButton = false;
  selected: string;
  selectedButton: any;

  controllers = [];

  photos: CarouselItem[];
  videos: CarouselItem[];
  plans: CarouselItem[];
  tour360: CarouselItem[];

  modalOpened: boolean;
  index = 0;
  indexDefault = 0;

  constructor(private snackbarService: SnackbarService) { }

  static VideoURLToThumbnail(url: string) {
    if (url) {
      if (url.toLowerCase().includes('chiligumvideos'.toLowerCase())) {
        return null;
      }
      const paths = url.split('v=');
      return `https://img.youtube.com/vi/${paths[paths.length - 1]}/sddefault.jpg`;
    }
  }

  static VideoURLToEmbedURL(url: string) {
    if (url) {
      if (url.toLowerCase().includes('chiligumvideos'.toLowerCase())) {
        return url;
      }
      if (url.toLowerCase().includes('youtube'.toLowerCase())) {
        const id = url
          .match(/v=(.*)/gi)[0]
          .replace(/&(.*)/gi, '')
          .replace('v=', '');
        return `https://www.youtube.com/embed/${id}`;
      }
      if (url.toLowerCase().includes('youtu.be'.toLowerCase())) {
        const path = url.split('/');
        return `https://www.youtube.com/embed/${path[path.length - 1]}`;
      }
      return url;
    }
  }

  static ValidateController(product, key: string, controller: any) {
    if (!getE(product, key)) {
      return null;
    }

    return controller && {}.toString.call(controller) === '[object Function]'
      ? controller(product)
      : controller;
  }

  init(product) {
    if (!product) {
      return;
    }
    this.loaded = true;
    this.enableShareButton = product.public_view;
    this.handlerControllers();
    this.photos = this.getPhotos();
    this.videos = this.getVideos();
    this.plans = this.getPlans();
    this.tour360 = this.getTour360();
    this.selected = this.setGaleryInit();
  }

  handleCloseModal() {
    this.index = this.indexDefault;

    this.plansView.index = this.indexDefault;
    this.plansView.init();
  }

  setPlanSelectedByProductPlans(index: number) {
    this.setSelected(this.getPlansController());
    this.openModal({ index });
  }

  getPlansController(): ControllerSelected {
    const selectedIndex = this.controllers.findIndex(
      (x) => x.value === 'floor_plans'
    );
    return {
      index: selectedIndex,
      value: this.controllers[selectedIndex].value,
    };
  }

  setGaleryInit() {
    if (this.product.photos?.length > 0) {
      return 'photos-carousel';
    }

    if (this.product.videos?.length > 0) {
      return 'video';
    }

    return 'map';
  }

  getPlans(): CarouselItem[] {
    return this.product.floor_plans
      ?.filter((p) => p && p.url)
      .map((p) => {
        const { short_description, url } = p;
        return {
          name: short_description,
          linkModal: url,
          linkHtml: url,
          linkThumbnail: url,
          alt: short_description,
        };
      });
  }

  getPhotos(): CarouselItem[] {
    return this.product.photos?.map((p) => {
      const { name, url, alternateText } = p;
      return {
        name,
        linkModal: url,
        linkHtml: url,
        linkThumbnail: url,
        alt: alternateText,
      };
    });
  }

  getVideos(): CarouselItem[] {
    return this.product.videos?.map((video) => {
      const { name, url } = video;

      return {
        name,
        linkModal: ProductGalleryComponent.VideoURLToEmbedURL(url),
        linkHtml: ProductGalleryComponent.VideoURLToEmbedURL(url),
        linkThumbnail: ProductGalleryComponent.VideoURLToThumbnail(url),
        alt: '',
      };
    });
  }

  getTour360(): CarouselItem[] {
    return this.product.tour360?.map((tour) => {
      const { name, url } = tour;
      return {
        name,
        linkModal: url,
        linkHtml: url,
        linkThumbnail: url,
        alt: '',
      };
    });
  }

  handlerControllers() {
    this.controllers = this.validatePlans()
      .map((f) =>
        ProductGalleryComponent.ValidateController(
          this.product,
          f.key,
          f.controller
        )
      )
      .filter((c) => c);

    this.selectedButton = this.controllers[0];
  }

  validatePlans() {
    const plans = getE(this.product, 'floor_plans', []).filter(
      (p) => p && p.url
    );

    return plans.length < 1
      ? ProductGalleryComponent.CONTROLLERS.filter(
        (controller) => controller.key !== 'floor_plans'
      )
      : ProductGalleryComponent.CONTROLLERS;
  }

  setSelected(evt: { index: number; value: string }) {
    this.selected = evt.value;
    this.selectedButton = this.controllers[evt.index];
  }

  openModal({ index }) {
    this.modalOpened = true;
    if (index !== undefined) {
      this.index = index;
    }
  }

  copyToCB() {
    if (!this.instanceDocument.getElementById('link') && this.product.public_view) {
      let host = 'https://www.lopes.com.br/imovel/';
      const username: any = document.querySelector('.sub-header-title .main-welcome').innerHTML.split(', ')[1];
      const input = this.instanceDocument.createElement('input');
      this.instanceDocument.body.appendChild(input);

      if (location.hostname === 'devfrontcrm.lpsbr.com' || location.hostname === 'localhost') {
        host = 'https://devfrontportal.lpsbr.com/imovel/';
      }

      let sku: any = window.location.href;
      sku = sku.split('/');
      sku = sku.reverse();
      sku =  sku[0];

      this.product.public_view === true && (this.product.product_status === 'Ativo' || this.product.product_status === 'ativo' || this.product.product_status === 'active' || this.product.product_status === 'Active')
        ? input.value = host + sku + '/?corretor=' + username.toLowerCase()
        : input.value = host + sku;

      input.select();
      input.id = 'link';
      this.instanceDocument.execCommand('copy');
      input.remove();
      this.snackbarService.show('Link copiado com sucesso!', 'success');
    }
  }
}
